import { useState } from 'react';
import {
  Plus,
  Zap,
  Trash2,
  Pencil,
  History,
  CheckCircle2,
  XCircle,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { useAutomations } from '@/lib/hooks/crm/useAutomations';
import type {
  CrmAutomationRule,
  CrmCreateAutomationInput,
  CrmAutomationExecution,
  CrmAutomationTriggerType,
  CrmAutomationActionType,
} from '@/lib/api';

const triggerLabels: Record<CrmAutomationTriggerType, string> = {
  deal_stage_changed: 'Deal Stage Changed',
  deal_created: 'Deal Created',
  contact_created: 'Contact Created',
  task_overdue: 'Task Overdue',
  field_updated: 'Field Updated',
};

const actionLabels: Record<CrmAutomationActionType, string> = {
  create_task: 'Create Task',
  update_field: 'Update Field',
  add_tag: 'Add Tag',
  send_notification: 'Send Notification',
};

const triggerTypes: CrmAutomationTriggerType[] = [
  'deal_stage_changed',
  'deal_created',
  'contact_created',
  'task_overdue',
  'field_updated',
];

const actionTypes: CrmAutomationActionType[] = [
  'create_task',
  'update_field',
  'add_tag',
  'send_notification',
];

const emptyForm: CrmCreateAutomationInput = {
  name: '',
  triggerType: 'deal_created',
  triggerConfig: {},
  actionType: 'create_task',
  actionConfig: {},
};

export function AutomationsView() {
  const { rules, loading, refetch, toggleRule, deleteRule, getExecutions } = useAutomations();
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<CrmAutomationRule | null>(null);
  const [form, setForm] = useState<CrmCreateAutomationInput>({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [executions, setExecutions] = useState<CrmAutomationExecution[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRuleName, setHistoryRuleName] = useState('');

  function openCreate() {
    setEditingRule(null);
    setForm({ ...emptyForm });
    setShowForm(true);
  }

  function openEdit(rule: CrmAutomationRule) {
    setEditingRule(rule);
    setForm({
      name: rule.name,
      triggerType: rule.triggerType,
      triggerConfig: rule.triggerConfig,
      actionType: rule.actionType,
      actionConfig: rule.actionConfig,
    });
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast({ title: 'Validation', description: 'Name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const api = (await import('@/lib/api')).default;
      if (editingRule) {
        await api.updateCrmAutomation(editingRule.id, form);
        toast({ title: 'Automation updated' });
      } else {
        await api.createCrmAutomation(form);
        toast({ title: 'Automation created' });
      }
      setShowForm(false);
      await refetch();
    } catch {
      toast({ title: 'Error', description: 'Failed to save automation', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(rule: CrmAutomationRule) {
    try {
      await toggleRule(rule.id);
      toast({ title: rule.isActive ? 'Automation disabled' : 'Automation enabled' });
    } catch {
      toast({ title: 'Error', description: 'Failed to toggle automation', variant: 'destructive' });
    }
  }

  async function handleDelete(rule: CrmAutomationRule) {
    try {
      await deleteRule(rule.id);
      toast({ title: 'Automation deleted' });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete automation', variant: 'destructive' });
    }
  }

  async function viewHistory(rule: CrmAutomationRule) {
    setHistoryRuleName(rule.name);
    setHistoryLoading(true);
    setShowHistory(true);
    try {
      const execs = await getExecutions(rule.id);
      setExecutions(execs);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load execution history',
        variant: 'destructive',
      });
    } finally {
      setHistoryLoading(false);
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="text-sm text-muted-foreground">
          {rules.length} automation{rules.length !== 1 ? 's' : ''}
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Add Automation
        </Button>
      </div>

      {/* Rule List */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : rules.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Zap className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">No automations yet</p>
            <p className="text-sm mt-1">Create rules to automate repetitive CRM tasks</p>
          </div>
        ) : (
          <div className="space-y-3">
            {rules.map((rule) => (
              <Card key={rule.id} className={!rule.isActive ? 'opacity-60' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Switch checked={rule.isActive} onCheckedChange={() => handleToggle(rule)} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{rule.name}</p>
                        {rule.isActive ? (
                          <Badge
                            variant="secondary"
                            className="bg-green-100 text-green-700 text-xs"
                          >
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">
                            Inactive
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <Badge variant="outline" className="text-xs">
                          {triggerLabels[rule.triggerType]}
                        </Badge>
                        <ArrowRight className="h-3 w-3" />
                        <Badge variant="outline" className="text-xs">
                          {actionLabels[rule.actionType]}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                        <span>{rule.executionCount} executions</span>
                        {rule.lastExecutedAt && (
                          <span>Last: {new Date(rule.lastExecutedAt).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(rule)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => viewHistory(rule)}>
                          <History className="h-4 w-4 mr-2" />
                          Execution History
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDelete(rule)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Automation' : 'New Automation'}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Auto-task on new deals"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>When (Trigger)</Label>
                <Select
                  value={form.triggerType}
                  onValueChange={(v) =>
                    setForm({
                      ...form,
                      triggerType: v as CrmAutomationTriggerType,
                      triggerConfig: {},
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {triggerTypes.map((t) => (
                      <SelectItem key={t} value={t}>
                        {triggerLabels[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Then (Action)</Label>
                <Select
                  value={form.actionType}
                  onValueChange={(v) =>
                    setForm({ ...form, actionType: v as CrmAutomationActionType, actionConfig: {} })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {actionTypes.map((a) => (
                      <SelectItem key={a} value={a}>
                        {actionLabels[a]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Trigger Config */}
            {form.triggerType === 'deal_stage_changed' && (
              <div className="grid gap-2">
                <Label className="text-xs">Target Stage ID (optional)</Label>
                <Input
                  value={(form.triggerConfig.toStageId as string) ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      triggerConfig: {
                        ...form.triggerConfig,
                        toStageId: e.target.value || undefined,
                      },
                    })
                  }
                  placeholder="Leave empty for any stage change"
                />
              </div>
            )}

            {form.triggerType === 'field_updated' && (
              <div className="grid gap-2">
                <Label className="text-xs">Field Name</Label>
                <Input
                  value={(form.triggerConfig.fieldName as string) ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      triggerConfig: { ...form.triggerConfig, fieldName: e.target.value },
                    })
                  }
                  placeholder="e.g. lifecycle_stage"
                />
              </div>
            )}

            {/* Action Config */}
            {form.actionType === 'create_task' && (
              <>
                <div className="grid gap-2">
                  <Label className="text-xs">Task Title</Label>
                  <Input
                    value={(form.actionConfig.title as string) ?? ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        actionConfig: { ...form.actionConfig, title: e.target.value },
                      })
                    }
                    placeholder="Follow up on new deal"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label className="text-xs">Priority</Label>
                    <Select
                      value={(form.actionConfig.priority as string) ?? 'medium'}
                      onValueChange={(v) =>
                        setForm({ ...form, actionConfig: { ...form.actionConfig, priority: v } })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label className="text-xs">Due in (days)</Label>
                    <Input
                      type="number"
                      value={(form.actionConfig.dueDateOffset as number) ?? ''}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          actionConfig: {
                            ...form.actionConfig,
                            dueDateOffset: e.target.value ? Number(e.target.value) : undefined,
                          },
                        })
                      }
                      placeholder="3"
                    />
                  </div>
                </div>
              </>
            )}

            {form.actionType === 'add_tag' && (
              <div className="grid gap-2">
                <Label className="text-xs">Tag to Add</Label>
                <Input
                  value={(form.actionConfig.tag as string) ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      actionConfig: { ...form.actionConfig, tag: e.target.value },
                    })
                  }
                  placeholder="e.g. hot-lead"
                />
              </div>
            )}

            {form.actionType === 'update_field' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label className="text-xs">Field Name</Label>
                  <Input
                    value={(form.actionConfig.fieldName as string) ?? ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        actionConfig: { ...form.actionConfig, fieldName: e.target.value },
                      })
                    }
                    placeholder="e.g. status"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-xs">New Value</Label>
                  <Input
                    value={(form.actionConfig.fieldValue as string) ?? ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        actionConfig: { ...form.actionConfig, fieldValue: e.target.value },
                      })
                    }
                    placeholder="e.g. active"
                  />
                </div>
              </div>
            )}

            {form.actionType === 'send_notification' && (
              <>
                <div className="grid gap-2">
                  <Label className="text-xs">Notification Title</Label>
                  <Input
                    value={(form.actionConfig.title as string) ?? ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        actionConfig: { ...form.actionConfig, title: e.target.value },
                      })
                    }
                    placeholder="New deal created"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-xs">Message</Label>
                  <Input
                    value={(form.actionConfig.message as string) ?? ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        actionConfig: { ...form.actionConfig, message: e.target.value },
                      })
                    }
                    placeholder="A new deal was created in your pipeline"
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingRule ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Execution History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Execution History: {historyRuleName}</DialogTitle>
          </DialogHeader>

          <div className="max-h-[400px] overflow-y-auto">
            {historyLoading ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : executions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No executions yet</p>
            ) : (
              <div className="space-y-2">
                {executions.map((exec) => (
                  <div key={exec.id} className="flex items-center gap-3 border rounded-lg p-3">
                    {exec.status === 'success' ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs">
                        <Badge
                          variant={exec.status === 'success' ? 'secondary' : 'destructive'}
                          className="text-xs"
                        >
                          {exec.status}
                        </Badge>
                        <span className="text-muted-foreground">
                          {exec.triggerEntityType} • {new Date(exec.executedAt).toLocaleString()}
                        </span>
                      </div>
                      {exec.errorMessage && (
                        <p className="text-xs text-red-600 mt-1 truncate">{exec.errorMessage}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
